"use client"

import Link from "next/link"
import { motion, MotionConfig, useReducedMotion, type Variants } from "framer-motion"
import { useTranslation } from "@/lib/i18n/provider"
import { LanguageSwitcherCompact } from "@/components/i18n/language-switcher"

// ─── Motion recipes (Jakub Krehel — production polish) ─────────────────────
// Enter: opacity + translateY + subtle blur. Spring, no bounce.
// Exit animations stay subtler than enters (Jakub rule).

const enterVariant: Variants = {
  hidden: { opacity: 0, y: 12, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { type: "spring", duration: 0.55, bounce: 0 },
  },
}

// Parent container that staggers its children on viewport entry.
const staggerParent = (delay = 0, step = 0.08): Variants => ({
  hidden: {},
  visible: {
    transition: {
      delayChildren: delay,
      staggerChildren: step,
    },
  },
})

// Hover lift (Jakub: 4-6px, spring, never bouncy)
const hoverLift = {
  y: -4,
  transition: { type: "spring" as const, stiffness: 300, damping: 22 },
}

// CTA tap feedback (Emil: restrained tactile response for frequent interactions)
const tapShrink = { scale: 0.97 }

export default function HomePage() {
  const { t } = useTranslation()
  const reduced = useReducedMotion()

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
        {/* Subtle background decorations — softly floating blobs */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white to-violet-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" />
        <div
          className={`absolute top-0 left-1/4 h-[600px] w-[600px] rounded-full bg-indigo-200/40 dark:bg-indigo-500/10 blur-[140px] ${
            reduced ? "" : "animate-slow-float"
          }`}
        />
        <div
          className={`absolute bottom-0 right-1/4 h-[600px] w-[600px] rounded-full bg-violet-200/40 dark:bg-violet-500/10 blur-[140px] ${
            reduced ? "" : "animate-slow-float"
          }`}
          style={{ animationDelay: "-4.5s" }}
        />

        {/* Navigation */}
        <header className="relative z-20">
          <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/20">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-lg font-semibold tracking-tight text-foreground">AgentHub</span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcherCompact />
              <Link
                href="/login"
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground"
              >
                {t.common.signIn}
              </Link>
              <motion.div whileTap={tapShrink}>
                <Link
                  href="/register"
                  className="inline-block rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-md shadow-primary/20 transition-all duration-150 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30"
                >
                  {t.common.getStarted}
                </Link>
              </motion.div>
            </div>
          </nav>
        </header>

        {/* Hero */}
        <main className="relative z-10 mx-auto max-w-7xl px-6 pt-20 pb-16">
          <motion.div
            variants={staggerParent(0.1, 0.1)}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-4xl text-center"
          >
            {/* Badge */}
            <motion.div
              variants={enterVariant}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs text-muted-foreground shadow-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              {t.home.badge}
            </motion.div>

            {/* Headline — gradient line gets a slow shimmer (Jhey touch) */}
            <motion.h1
              variants={enterVariant}
              className="mb-6 text-5xl font-bold tracking-tight sm:text-6xl md:text-7xl"
            >
              <span className="block text-foreground">{t.home.headlineLine1}</span>
              <span className="animate-gradient-shift block bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500 bg-clip-text text-transparent">
                {t.home.headlineLine2}
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={enterVariant}
              className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground sm:text-xl"
            >
              {t.home.subheadline}
            </motion.p>

            {/* CTAs */}
            <motion.div
              variants={enterVariant}
              className="flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              <motion.div whileTap={tapShrink} className="w-full sm:w-auto">
                <Link
                  href="/register"
                  className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/30 sm:w-auto"
                >
                  {t.home.ctaPrimary}
                  <svg className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </motion.div>
              <motion.div whileTap={tapShrink} className="w-full sm:w-auto">
                <Link
                  href="/login"
                  className="inline-flex w-full items-center justify-center rounded-xl border border-border bg-card px-8 py-4 text-sm font-semibold text-foreground shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-accent hover:shadow-md sm:w-auto"
                >
                  {t.home.ctaSecondary}
                </Link>
              </motion.div>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              variants={enterVariant}
              className="mt-12 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground"
            >
              {[t.home.trust1, t.home.trust2, t.home.trust3].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </div>
              ))}
            </motion.div>
          </motion.div>
        </main>

        {/* Mission / Concept */}
        <section className="relative z-10 border-t border-border/60 bg-gradient-to-b from-transparent to-indigo-50/30 dark:to-slate-900/50">
          <div className="mx-auto max-w-6xl px-6 py-24">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ type: "spring", duration: 0.6, bounce: 0 }}
              className="mx-auto max-w-2xl text-center"
            >
              <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
                {t.home.missionTitle}
              </h2>
              <p className="text-base text-muted-foreground sm:text-lg">
                {t.home.missionKicker}
              </p>
            </motion.div>

            <motion.div
              variants={staggerParent(0.1, 0.1)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3"
            >
              <MissionCard
                emoji="⚠️"
                title={t.home.problemTitle}
                body={t.home.problemBody}
                accent="from-rose-500/10 to-orange-500/5 border-rose-200/50 dark:border-rose-900/30"
              />
              <MissionCard
                emoji="🎯"
                title={t.home.approachTitle}
                body={t.home.approachBody}
                accent="from-indigo-500/10 to-violet-500/5 border-indigo-200/50 dark:border-indigo-900/30"
              />
              <MissionCard
                emoji="⚡"
                title={t.home.diffTitle}
                body={t.home.diffBody}
                accent="from-emerald-500/10 to-teal-500/5 border-emerald-200/50 dark:border-emerald-900/30"
              />
            </motion.div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="relative z-10 border-t border-border/60">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ type: "spring", duration: 0.6, bounce: 0 }}
              className="mx-auto mb-16 max-w-2xl text-center"
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                {t.home.featuresTitle}
              </h2>
            </motion.div>
            <motion.div
              variants={staggerParent(0, 0.06)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-3"
            >
              <FeatureCard icon="◆" accent="from-indigo-500 to-indigo-600" title={t.home.feature1Title} description={t.home.feature1Desc} />
              <FeatureCard icon="◈" accent="from-violet-500 to-violet-600" title={t.home.feature2Title} description={t.home.feature2Desc} />
              <FeatureCard icon="◇" accent="from-cyan-500 to-cyan-600" title={t.home.feature3Title} description={t.home.feature3Desc} />
              <FeatureCard icon="◉" accent="from-pink-500 to-rose-600" title={t.home.feature4Title} description={t.home.feature4Desc} />
              <FeatureCard icon="◊" accent="from-amber-500 to-orange-600" title={t.home.feature5Title} description={t.home.feature5Desc} />
              <FeatureCard icon="▲" accent="from-emerald-500 to-teal-600" title={t.home.feature6Title} description={t.home.feature6Desc} />
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section className="relative z-10 border-t border-border/60 bg-gradient-to-b from-transparent to-violet-50/30 dark:to-slate-900/50">
          <div className="mx-auto max-w-6xl px-6 py-24">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ type: "spring", duration: 0.6, bounce: 0 }}
              className="mx-auto mb-16 max-w-2xl text-center"
            >
              <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
                {t.home.howTitle}
              </h2>
              <p className="text-base text-muted-foreground sm:text-lg">
                {t.home.howKicker}
              </p>
            </motion.div>

            <motion.div
              variants={staggerParent(0, 0.1)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
            >
              <StepCard step={1} title={t.home.step1Title} body={t.home.step1Body} />
              <StepCard step={2} title={t.home.step2Title} body={t.home.step2Body} />
              <StepCard step={3} title={t.home.step3Title} body={t.home.step3Body} />
              <StepCard step={4} title={t.home.step4Title} body={t.home.step4Body} />
            </motion.div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="relative z-10 border-t border-border/60">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ type: "spring", duration: 0.6, bounce: 0 }}
            className="mx-auto max-w-4xl px-6 py-24 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              {t.home.closingTitle}
            </h2>
            <p className="mx-auto mb-10 max-w-xl text-base text-muted-foreground sm:text-lg">
              {t.home.closingBody}
            </p>
            <motion.div whileTap={tapShrink} className="inline-block">
              <Link
                href="/register"
                className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 px-10 py-4 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/30"
              >
                {t.home.closingCta}
                <svg className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 border-t border-border">
          <div className="mx-auto max-w-7xl px-6 py-8 text-center text-xs text-muted-foreground">
            {t.home.footer} · multi-ai.it-contrast.com.ua
          </div>
        </footer>
      </div>
    </MotionConfig>
  )
}

// ─── Cards — all use Jakub enter recipe + subtle hover lift ────────────────

function MissionCard({
  emoji,
  title,
  body,
  accent,
}: {
  emoji: string
  title: string
  body: string
  accent: string
}) {
  return (
    <motion.div
      variants={enterVariant}
      whileHover={hoverLift}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${accent} p-6 shadow-sm will-change-transform`}
    >
      <div className="mb-4 text-3xl">{emoji}</div>
      <h3 className="mb-3 text-lg font-semibold text-foreground">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </motion.div>
  )
}

function FeatureCard({
  icon,
  accent,
  title,
  description,
}: {
  icon: string
  accent: string
  title: string
  description: string
}) {
  return (
    <motion.div
      variants={enterVariant}
      whileHover={hoverLift}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow duration-200 will-change-transform hover:shadow-md hover:border-primary/30"
    >
      <motion.div
        whileHover={{ scale: 1.06, rotate: 2 }}
        transition={{ type: "spring", stiffness: 300, damping: 16 }}
        className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-lg text-white shadow-md`}
      >
        {icon}
      </motion.div>
      <h3 className="mb-2 text-base font-semibold text-foreground">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
    </motion.div>
  )
}

function StepCard({
  step,
  title,
  body,
}: {
  step: number
  title: string
  body: string
}) {
  return (
    <motion.div
      variants={enterVariant}
      whileHover={hoverLift}
      className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm will-change-transform"
    >
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white shadow-md shadow-indigo-500/20">
        {step}
      </div>
      <h3 className="mb-2 text-base font-semibold text-foreground">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </motion.div>
  )
}
